import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Formik } from "formik";
import {
  Container,
  Grid,
  IconButton,
  TextField,
  Typography,
  Backdrop,
  Box,
  Button,
  FormControlLabel,
  Modal,
  Switch
} from "@mui/material";

import { postNotification } from "../../../components/utilities/Notifications";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";

import {
  BCPSquarePlus,
  BCPXmark
} from "../../../components/icons/bcp/BCPIcons";

import {
  getArmies,
  getGameSystems
} from "../../../components/api/APIUtilities";
import ListResponsive from "../../../components/other/ListResponsive";
import { AdminTabs } from "../Admin";
import GameSystemPicker from "../../../components/utilities/gamesystempickers/GameSystemPicker";
import GenericMultiSelect from "../../../components/utilities/GenericMultiSelect";
import { postData } from "../../../components/utilities/UtilityFunctions";
import ImageUploader, {
  uploadBas64ToS3
} from "../../../components/utilities/ImageUploader";
import FileUploader, {
  uploadFileToS3
} from "../../../components/utilities/FileUploader";

const armyLabel = (row) => {
  return row.superFactionObjects?.map((army) => army.name).join(", ");
};

export default function AdminSubFactions(props) {
  const { t } = useTranslation();
  const { activeTab, handleTabChange, authContext } = props;

  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subLoading, setSubLoading] = useState(true);

  const [gameSystems, setGameSystems] = useState(null);
  const [gameSystem, setGameSystem] = useState(null);

  const [subFactions, setSubFactions] = useState(null);
  const [subFaction, setSubFaction] = useState(null);

  const [armies, setArmies] = useState([]);
  const [armyDict, setArmyDict] = useState({});

  useEffect(() => {
    const go = async () => {
      var apiGameSystems = await getGameSystems();
      if (
        !authContext?.authState?.identity?.isAdmin &&
        authContext?.authState?.identity?.adminGameSystems?.length > 0
      ) {
        apiGameSystems = apiGameSystems.filter((el) =>
          authContext.authState.identity.adminGameSystems.includes(el.id)
        );
      }
      setGameSystems(apiGameSystems);
      setLoading(false);
    };
    go(); // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const go = async () => {
      setSubLoading(true);
      setSubFactions(null);
      setArmies([]);
      setArmyDict({});
      if (gameSystem) {
        var apiArmies = await getArmies({
          gameType: gameSystem.code,
          "collections[]": ["subFactions"]
        });
        var newArmyDict = {};
        var newArmies = [];
        var newSubFactionDict = {};
        var newSubFactions = [];

        var gameSystemChildFactions = false;
        for (let army of apiArmies) {
          var item = {
            value: army.id,
            label: army.name,
            children: [{ value: army.id, label: army.name }]
          };
          newArmyDict[army.id] = {
            id: army.id,
            label: army.name
          };
          if (army.childArmies?.length) {
            gameSystemChildFactions = true;
            item.children = [
              ...item.children,
              ...army.childArmies.map((child) => {
                newArmyDict[child.id] = {
                  id: child.id,
                  label: child.name
                };

                return {
                  value: child.id,
                  label: child.name
                };
              })
            ];

            for (let _army of army.childArmies) {
              if (_army.subFactions?.length) {
                for (let newSubFaction of _army.subFactions) {
                  if (!newSubFactionDict[newSubFaction.id]) {
                    newSubFactionDict[newSubFaction.id] = newSubFaction;
                    newSubFactions.push(newSubFaction);
                  }
                }
              }
            }
          }
          newArmies.push(item);
          if (army.subFactions?.length) {
            for (let newSubFaction of army.subFactions) {
              if (!newSubFactionDict[newSubFaction.id]) {
                newSubFactionDict[newSubFaction.id] = newSubFaction;
                newSubFactions.push(newSubFaction);
              }
            }
          }
        }

        if (!gameSystemChildFactions) {
          newArmies = newArmies.map((item) => {
            delete item.children;
            return item;
          });
        }
        setArmies(newArmies);

        newSubFactions.forEach((newSubFaction) => {
          newSubFaction.superFactionObjects = newSubFaction.superFactions.map(
            (superFaction) => {
              return {
                id: newArmyDict[superFaction].id,
                name: newArmyDict[superFaction].label
              };
            }
          );
          newSubFaction.superFactionsLabel = armyLabel(newSubFaction);
        });

        newSubFactions.sort((a, b) => {
          var nameA = a.name.toLowerCase();
          var nameB = b.name.toLowerCase();
          if (nameA > nameB) return 1;
          if (nameA < nameB) return -1;
          return 0;
        });
        setSubFactions(newSubFactions);
        setArmyDict(newArmyDict);
      }

      setSubLoading(false);
    };
    go();
  }, [gameSystem]);

  return (
    <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
      <Grid container sx={{ gap: 2 }}>
        <Grid
          item
          xs={12}
          sx={{
            p: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 1,
            backgroundColor: "background.secondary"
          }}
        >
          <Typography variant="h3">{t("pages.admin.adminSettings")}</Typography>

          <AdminTabs
            activeTab={activeTab}
            handleTabChange={handleTabChange}
            authContext={authContext}
          />
        </Grid>
        {gameSystems && (
          <Grid
            item
            xs={12}
            sx={{
              p: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              borderRadius: 1,
              boxShadow: 1,
              backgroundColor: "background.secondary"
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <GameSystemPicker
                  name="gameType"
                  value={gameSystem?.code || ""}
                  handleChange={(e) => {
                    if (e.target.value) {
                      gameSystems.forEach((gs) => {
                        if (gs.code === e.target.value) {
                          setGameSystem(gs);
                          return true;
                        }
                      });
                    }
                  }}
                  gameSystems={gameSystems}
                />
              </Grid>
            </Grid>
          </Grid>
        )}

        {gameSystem && (
          <Grid
            item
            xs={12}
            sx={{
              p: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              borderRadius: 1,
              boxShadow: 1,
              backgroundColor: "background.secondary"
            }}
          >
            <ListResponsive
              originalList={subLoading ? null : subFactions}
              doSearch
              doPages
              initialRowsPerPage={24}
              actionButton={[
                {
                  props: {
                    variant: "contained",
                    color: "primary",
                    onClick: () => {
                      setSubFaction({});
                    },
                    startIcon: <BCPSquarePlus />
                  },
                  label: "Add Sub Faction"
                }
              ]}
              searchProps={{
                label: "Search Sub Factions",
                performSearch: (originalList, searchText) => {
                  if (!searchText || searchText === "") {
                    return originalList;
                  } else {
                    var filteredList = originalList.filter(function (el) {
                      if (el?.name?.toLowerCase().includes(searchText))
                        return true;
                      if (
                        el?.superFactionsLabel
                          ?.toLowerCase()
                          .includes(searchText)
                      )
                        return true;

                      return false;
                    });
                    return filteredList;
                  }
                }
              }}
              rowProps={{
                mapFunction: (row, index) => (
                  <Grid
                    key={index}
                    item
                    xs={12}
                    onClick={() => {
                      setSubFaction(row);
                    }}
                    sx={{
                      p: 1,
                      mb: 1,
                      border: "1px solid rgba(0, 0, 0, 0.12)",
                      borderRadius: 1,
                      boxShadow: 1,
                      "&:hover": {
                        cursor: "pointer"
                      }
                    }}
                  >
                    <Typography variant="body1">{row.name}</Typography>
                    <Typography variant="caption">
                      {row.superFactionsLabel}
                    </Typography>
                  </Grid>
                )
              }}
            />
          </Grid>
        )}
      </Grid>

      <SubFactionModal
        gameSystem={gameSystem}
        subFaction={subFaction}
        setSubFaction={setSubFaction}
        subFactions={subFactions}
        setSubFactions={setSubFactions}
        armies={armies}
        armyDict={armyDict}
        setProcessing={setProcessing}
        t={t}
      />

      {Boolean(processing || loading) && <ModalLoadingFull />}
    </Container>
  );
}

function SubFactionModal(props) {
  const {
    gameSystem,
    subFaction,
    setSubFaction,
    subFactions,
    setSubFactions,
    armies,
    armyDict,
    setProcessing,
    t
  } = props;

  return (
    <Modal
      disableAutoFocus
      open={Boolean(subFaction)}
      onClose={() => {
        setSubFaction(null);
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500
      }}
      sx={{ zIndex: 9999 }}
    >
      {subFaction ? (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: ["95vw", "85vw", 600],
            maxHeight: "95vh",
            overflowY: "scroll",
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={() => {
              setSubFaction(null);
            }}
          >
            <BCPXmark />
          </IconButton>
          <Grid
            container
            spacing={2}
            sx={{
              pb: 1,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
            }}
          >
            <Grid item xs>
              <Typography variant="h4" component="h2">
                {subFaction.id ? "Update Sub Faction" : "Create Sub Faction"}
              </Typography>
            </Grid>
          </Grid>
          <Formik
            initialValues={{
              id: subFaction?.id || null,
              name: subFaction?.name || "",
              active:
                subFaction?.active !== undefined ? subFaction.active : true,
              superFactions: subFaction?.superFactions?.length
                ? subFaction.superFactions
                : []
            }}
            enableReinitialize
            validationSchema={Yup.object().shape({
              name: Yup.string()
                .min(3, "Minimum 3 Characters!")
                .max(255, "Maximum 255 Characters!")
                .required("Required")
            })}
            onSubmit={async (values) => {
              var apiData;
              var newImageFile, newIconFile;
              setProcessing(true);
              try {
                var sanitizedValues = {
                  name: values.name,
                  active: values.active,
                  superFactions: values.superFactions
                };
                if (gameSystem) {
                  sanitizedValues.gameSystemId = gameSystem.id;
                }

                if (sanitizedValues.subFactionImage) {
                  // upload image to s3
                  newImageFile = await uploadBas64ToS3(
                    sanitizedValues.subFactionImage
                  );
                  delete sanitizedValues.subFactionImage;
                  if (newImageFile.fileId) {
                    sanitizedValues.imageFileId = newImageFile.fileId;
                  }
                }

                if (sanitizedValues.subFactionIcon) {
                  // upload icon to s3
                  newIconFile = await uploadFileToS3(
                    sanitizedValues.subFactionIcon
                  );
                  delete sanitizedValues.subFactionIcon;
                  if (newIconFile.fileId) {
                    sanitizedValues.iconFileId = newIconFile.fileId;
                  }
                }

                if (values.id) {
                  var body = {
                    set: sanitizedValues
                  };
                  apiData = await postData(`/subfactions/${values.id}`, {
                    body: body
                  });
                  postNotification(
                    "success",
                    t("notifications.success.subFactionUpdated")
                  );
                } else {
                  apiData = await postData(`/subfactions`, {
                    body: sanitizedValues
                  });
                }

                apiData.superFactionObjects = apiData.superFactions.map(
                  (superFaction) => {
                    return {
                      id: armyDict[superFaction].id,
                      name: armyDict[superFaction].label
                    };
                  }
                );
                apiData.superFactionsLabel = armyLabel(apiData);

                var newSubFactions;
                if (subFaction.id) {
                  newSubFactions = [];
                  subFactions.forEach((currentsubFaction) => {
                    if (currentsubFaction.id !== subFaction.id) {
                      newSubFactions.push(currentsubFaction);
                    } else {
                      newSubFactions.push(apiData);
                    }
                  });
                } else {
                  newSubFactions = [apiData, ...subFactions];
                }
                setSubFactions(newSubFactions);
                setSubFaction(null);
                setProcessing(false);
                return;
              } catch (err) {
                postNotification("error", err);
                setProcessing(false);
              }
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              touched,
              values,
              setFieldValue
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <TextField
                      name="name"
                      label="Name"
                      value={values.name}
                      variant="outlined"
                      fullWidth
                      required
                      margin="normal"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(touched.name && errors.name)}
                      helperText={touched.name && errors.name}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <GenericMultiSelect
                      name="superFactions"
                      label="Factions"
                      value={values.superFactions}
                      setFieldValue={setFieldValue}
                      listDict={armyDict}
                      list={armies}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      label="Active"
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)"
                        }
                      }}
                      control={
                        <Switch
                          checked={values.active}
                          onChange={(e) => {
                            setFieldValue("active", !values.active);
                          }}
                        />
                      }
                      labelPlacement="start"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body1">Image</Typography>
                    <ImageUploader
                      values={values}
                      label="Image"
                      name="subFactionImage"
                      fileId={values.imageFileId}
                      setFieldValue={setFieldValue}
                      defaultUrl={
                        subFaction?.imageFile?.cloudFrontDomain
                          ? `${subFaction?.imageFile?.cloudFrontDomain}${subFaction?.imageFile?.filename}`
                          : null
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body1">Icon</Typography>
                    <FileUploader
                      value={values?.subFactionIcon}
                      label="Icon"
                      name="subFactionIcon"
                      setFieldValue={setFieldValue}
                      accept="image/svg+xml"
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setSubFaction(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      type="submit"
                      disabled={!values.name}
                    >
                      Save
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Box>
      ) : (
        <></>
      )}
    </Modal>
  );
}
